import { v4 as uuidv4 } from "uuid";

export const DUST_ANONYMOUS_ID_COOKIE = "_dust_aid";

const ANONYMOUS_ID_MAX_AGE_SECONDS = 31536000; // 1 year

const COOKIE_DOMAIN_PROBE = "_dust_cookie_domain_probe";

let cachedRootCookieDomain: string | null | undefined;

/**
 * Returns the widest domain the browser accepts a cookie on, so cookies are
 * shared across subdomains (`.example.com` from `app.example.com`).
 * Returns null on localhost, or when no parent domain accepts one.
 */
export function getRootCookieDomain(): string | null {
  if (typeof window === "undefined") {
    return null;
  }

  if (cachedRootCookieDomain === undefined) {
    cachedRootCookieDomain = probeRootCookieDomain(window.location.hostname);
  }
  return cachedRootCookieDomain;
}

// Probing rather than taking the last two labels, because a cookie on a public
// suffix such as `.run.app` is silently dropped, the same way a hardcoded
// domain the site is not on is.
function probeRootCookieDomain(hostname: string): string | null {
  const labels = hostname.split(".");
  for (let i = labels.length - 2; i >= 0; i--) {
    const domain = `.${labels.slice(i).join(".")}`;
    document.cookie = `${COOKIE_DOMAIN_PROBE}=1; domain=${domain}; path=/; SameSite=Lax; Secure`;
    if (document.cookie.split("; ").includes(`${COOKIE_DOMAIN_PROBE}=1`)) {
      document.cookie = `${COOKIE_DOMAIN_PROBE}=; domain=${domain}; path=/; max-age=0; SameSite=Lax; Secure`;
      return domain;
    }
  }
  return null;
}

/**
 * Returns the root cookie domain for PostHog cross-subdomain tracking, or
 * `undefined` when there is none (localhost/dev).
 */
export function getPostHogCookieDomain(): string | undefined {
  const domain = getRootCookieDomain();
  return domain ?? undefined;
}

/**
 * Builds the cookie string for `_dust_aid` including the domain attribute
 * when applicable (production multi-subdomain setup).
 */
export function buildAnonymousIdCookieString(value: string): string {
  const domain = getRootCookieDomain();
  const domainPart = domain ? `; domain=${domain}` : "";
  return `${DUST_ANONYMOUS_ID_COOKIE}=${value}; path=/${domainPart}; SameSite=Lax; Secure; max-age=${ANONYMOUS_ID_MAX_AGE_SECONDS}`;
}

/**
 * Reads the `_dust_aid` cookie from `document.cookie`; if absent, generates a
 * UUIDv4, sets it as a first-party cookie, and returns it.
 *
 * The cookie is set on the root domain (see `getRootCookieDomain`) so it is
 * shared between the marketing site and the app.
 *
 * Client-side only. Returns `null` when called outside a browser context.
 */
export function getOrCreateAnonymousId(): string | null {
  if (typeof document === "undefined") {
    return null;
  }

  const existing = readAnonymousIdFromDocumentCookie();
  if (existing) {
    return existing;
  }

  const id = uuidv4();
  document.cookie = buildAnonymousIdCookieString(id);
  return id;
}

/**
 * Reads the `_dust_aid` value from `document.cookie`.
 * Returns `null` if the cookie is not present or we're not in a browser.
 */
function readAnonymousIdFromDocumentCookie(): string | null {
  if (typeof document === "undefined") {
    return null;
  }
  return parseAnonymousIdFromCookieString(document.cookie);
}

/**
 * Server-side helper: parses the `_dust_aid` value from a raw `Cookie` header.
 * Returns `null` if not found.
 */
export function readAnonymousIdFromCookies(
  cookieHeader: string | undefined
): string | null {
  if (!cookieHeader) {
    return null;
  }
  return parseAnonymousIdFromCookieString(cookieHeader);
}

function parseAnonymousIdFromCookieString(cookies: string): string | null {
  const prefix = `${DUST_ANONYMOUS_ID_COOKIE}=`;
  const match = cookies.split("; ").find((c) => c.startsWith(prefix));
  return match ? match.slice(prefix.length) : null;
}
